/**
 * FontManager - Comprehensive font loading and management system for EdgeDocs
 * 
 * Provides legal-compliant font loading, caching, and feature detection
 * Integrates with backend FontRegistry for proper font handling
 */

import { BackendFontService } from '../services/BackendFontService';

export interface FontSpec {
  family: string;
  weight: number | string;
  style: 'normal' | 'italic' | 'oblique';
  size: number;
  features?: string[]; // OpenType features
}

export interface LoadedFont {
  fontFace: FontFace;
  family: string;
  weight: string;
  style: string;
  features: Set<string>; // Supported OpenType features
  scripts: Set<string>; // Supported scripts (Latn, Cyrl, etc.)
  status: 'loading' | 'loaded' | 'error';
  loadTime: number;
  fileSize: number;
  version?: string;
}

export interface FontLoadingConfig {
  supportedFormats: string[];
  harfbuzzFeatures: string[];
  preloadFonts: string[];
  cacheStrategy: 'aggressive' | 'conservative' | 'minimal';
  fallbackStrategy: 'immediate' | 'delayed' | 'none';
}

export interface LegalFontStandards {
  courtRequiredFonts: {
    federal: string[];
    california: string[];
    newYork: string[];
    texas: string[];
    florida: string[];
  };
  firmCustomFonts: string[];
  professionalFonts: string[];
  fallbackChains: Map<string, string[]>;
}

export class FontManager {
  private loadedFonts: Map<string, LoadedFont> = new Map();
  private loadingPromises: Map<string, Promise<LoadedFont>> = new Map();
  private config: FontLoadingConfig;
  private legalStandards: LegalFontStandards;
  private fontCache: Map<string, ArrayBuffer> = new Map();
  private backendFontService: BackendFontService | null = null;
  
  // Performance tracking
  private loadingMetrics: {
    totalLoads: number;
    successfulLoads: number;
    failedLoads: number;
    averageLoadTime: number;
    cacheHitRate: number;
  } = {
    totalLoads: 0,
    successfulLoads: 0,
    failedLoads: 0,
    averageLoadTime: 0,
    cacheHitRate: 0
  };

  constructor(config: FontLoadingConfig) {
    this.config = config;
    this.legalStandards = this.initializeLegalStandards();
    this.initializeFontCache();
    this.preloadCriticalFonts();
  }

  /**
   * Initialize backend font service integration
   */
  async initializeBackendFonts(baseUrl: string, token: string): Promise<void> {
    try {
      this.backendFontService = new BackendFontService(baseUrl, token);
      await this.backendFontService.initialize();
    } catch (error) {
      console.warn('[FontManager] Backend font service initialization failed:', error);
      this.backendFontService = null;
    }
  }

  /**
   * Initialize legal font standards for court compliance
   */
  private initializeLegalStandards(): LegalFontStandards {
    const standards: LegalFontStandards = {
      courtRequiredFonts: {
        federal: ['Times New Roman', 'Times', 'serif'],
        california: ['Arial', 'Helvetica', 'sans-serif'],
        newYork: ['Times New Roman', 'Century Schoolbook', 'serif'],
        texas: ['Times New Roman', 'Arial', 'serif'],
        florida: ['Times New Roman', 'Courier New', 'serif']
      },
      firmCustomFonts: [
        'Minion Pro',      // Adobe professional serif
        'Trajan Pro',      // Professional caps font
        'Myriad Pro',      // Adobe sans-serif
        'Garamond Premier' // Classic legal serif
      ],
      professionalFonts: [
        'Times New Roman',
        'Arial',
        'Helvetica',
        'Courier New',
        'Georgia',
        'Verdana',
        'Calibri',
        'Cambria'
      ],
      fallbackChains: new Map()
    };

    // Initialize fallback chains
    standards.fallbackChains.set('Times New Roman', [
      'Times New Roman', 'Times', 'Liberation Serif', 'DejaVu Serif', 'serif'
    ]);
    standards.fallbackChains.set('Arial', [
      'Arial', 'Helvetica', 'Liberation Sans', 'DejaVu Sans', 'sans-serif'
    ]);
    standards.fallbackChains.set('Courier New', [
      'Courier New', 'Courier', 'Liberation Mono', 'DejaVu Sans Mono', 'monospace'
    ]);

    return standards;
  }

  /**
   * Initialize font caching system
   */
  private initializeFontCache(): void {
    // Check for existing font cache in IndexedDB
    if ('indexedDB' in window) {
      this.initializeIndexedDBCache();
    }
    
  }

  /**
   * Initialize IndexedDB for persistent font caching
   */
  private async initializeIndexedDBCache(): Promise<void> {
    try {
      const request = indexedDB.open('EdgeDocsFontCache', 1);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('fonts')) {
          const store = db.createObjectStore('fonts', { keyPath: 'id' });
          store.createIndex('family', 'family', { unique: false });
          store.createIndex('version', 'version', { unique: false });
        }
      };
      
    } catch (error) {
      console.warn('[FontManager] IndexedDB not available, using memory cache only:', error);
    }
  }

  /**
   * Preload critical fonts for immediate availability
   */
  private async preloadCriticalFonts(): Promise<void> {
    // Use only web-safe fonts that are guaranteed to be available
    const criticalFonts = [
      'serif',
      'sans-serif', 
      'monospace'
    ];
    
    
    const loadPromises = criticalFonts.map(family => 
      this.loadFont({ family, weight: 'normal', style: 'normal', size: 16 })
        .catch(error => {
          return null;
        })
    );

    try {
      await Promise.allSettled(loadPromises);
    } catch (error) {
      console.warn('[FontManager] Font initialization completed with warnings:', error);
    }
  }

  /**
   * Load a font with comprehensive error handling and caching
   */
  public async loadFont(fontSpec: FontSpec): Promise<LoadedFont> {
    const fontKey = this.getFontKey(fontSpec);
    
    // Return cached font if available
    if (this.loadedFonts.has(fontKey)) {
      const cached = this.loadedFonts.get(fontKey)!;
      this.loadingMetrics.cacheHitRate = 
        (this.loadingMetrics.cacheHitRate * this.loadingMetrics.totalLoads + 1) / 
        (this.loadingMetrics.totalLoads + 1);
      return cached;
    }

    // If backend font service is available, use it
    if (this.backendFontService) {
      return this.loadFontViaBackend(fontSpec);
    }

    // Fallback to original loading logic
    return this.loadFontLegacy(fontSpec);
  }

  /**
   * Load font via backend font service (preferred method)
   */
  private async loadFontViaBackend(fontSpec: FontSpec): Promise<LoadedFont> {
    try {
      const weight = typeof fontSpec.weight === 'string' ? 400 : fontSpec.weight;
      const style = fontSpec.style === 'oblique' ? 'italic' : fontSpec.style;
      
      const backendFont = this.backendFontService!.resolveFontRequest(
        fontSpec.family, 
        weight, 
        style as 'normal' | 'italic'
      );

      const loadedFont: LoadedFont = {
        fontFace: null as any, // Backend handles the actual font loading
        family: backendFont.family,
        weight: backendFont.weight.toString(),
        style: backendFont.style,
        features: new Set(fontSpec.features || []),
        scripts: new Set(['Latn']),
        status: 'loaded',
        loadTime: 0,
        fileSize: 0
      };

      const fontKey = this.getFontKey(fontSpec);
      this.loadedFonts.set(fontKey, loadedFont);
      this.loadingMetrics.successfulLoads++;
      this.loadingMetrics.totalLoads++;

      return loadedFont;

    } catch (error) {
      this.loadingMetrics.failedLoads++;
      this.loadingMetrics.totalLoads++;
      return this.getFallbackFont(fontSpec);
    }
  }

  /**
   * Legacy font loading method (fallback)
   */
  private async loadFontLegacy(fontSpec: FontSpec): Promise<LoadedFont> {
    const fontKey = this.getFontKey(fontSpec);
    
    // Return existing loading promise if font is currently loading
    if (this.loadingPromises.has(fontKey)) {
      return this.loadingPromises.get(fontKey)!;
    }

    // Start loading the font
    const loadingPromise = this.loadFontInternal(fontSpec);
    this.loadingPromises.set(fontKey, loadingPromise);

    try {
      const loadedFont = await loadingPromise;
      this.loadedFonts.set(fontKey, loadedFont);
      this.loadingMetrics.successfulLoads++;
      return loadedFont;
    } catch (error) {
      this.loadingMetrics.failedLoads++;
      
      // Return fallback font
      return this.getFallbackFont(fontSpec);
    } finally {
      this.loadingPromises.delete(fontKey);
      this.loadingMetrics.totalLoads++;
    }
  }

  /**
   * Internal font loading implementation
   */
  private async loadFontInternal(fontSpec: FontSpec): Promise<LoadedFont> {
    const startTime = performance.now();
    
    try {
      // Try to load from system fonts first
      const systemFont = await this.loadSystemFont(fontSpec);
      if (systemFont) {
        return systemFont;
      }

      // Try to load from web fonts
      const webFont = await this.loadWebFont(fontSpec);
      if (webFont) {
        return webFont;
      }

      // This is expected for many fonts, so don't throw an error
      throw new Error(`Font ${fontSpec.family} not available`);
    } finally {
      const loadTime = performance.now() - startTime;
      this.updateLoadingMetrics(loadTime);
    }
  }

  /**
   * Load font from system fonts with proper generic family handling
   */
  private async loadSystemFont(fontSpec: FontSpec): Promise<LoadedFont | null> {
    const family = fontSpec.family;
    
    // Handle CSS generic families - these don't need FontFace loading
    if (this.isGenericFamily(family)) {
      return {
        fontFace: null as any, // Generic families don't use FontFace
        family: family,
        weight: fontSpec.weight.toString(),
        style: fontSpec.style,
        features: new Set(fontSpec.features || []),
        scripts: new Set(['Latn']),
        status: 'loaded',
        loadTime: 0,
        fileSize: 0
      };
    }

    // For specific font names, try to load them
    try {
      // Check if specific font is available
      if (!this.isFontAvailable(family)) {
        return null;
      }

      const fontFace = new FontFace(
        family,
        `local("${family}")`,
        {
          weight: fontSpec.weight.toString(),
          style: fontSpec.style
        }
      );

      await fontFace.load();
      document.fonts.add(fontFace);

      return {
        fontFace,
        family: family,
        weight: fontSpec.weight.toString(),
        style: fontSpec.style,
        features: new Set(fontSpec.features || []),
        scripts: new Set(['Latn']),
        status: 'loaded',
        loadTime: 0,
        fileSize: 0
      };
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if a font family is a CSS generic family
   */
  private isGenericFamily(family: string): boolean {
    const genericFamilies = ['serif', 'sans-serif', 'monospace', 'cursive', 'fantasy', 'system-ui'];
    return genericFamilies.includes(family.toLowerCase());
  }

  /**
   * Check if a font is available in the system
   */
  private isFontAvailable(fontFamily: string): boolean {
    // Skip generic families - they're always "available"
    if (this.isGenericFamily(fontFamily)) {
      return true;
    }

    try {
      // Use a more reliable font detection method
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) return false;

      // Test text that should render differently in different fonts
      const testText = 'mmmmmmmmmmlli';
      const testSize = '72px';
      
      // Measure with fallback font
      context.font = `${testSize} monospace`;
      const fallbackWidth = context.measureText(testText).width;
      
      // Measure with requested font + fallback
      context.font = `${testSize} "${fontFamily}", monospace`;
      const testWidth = context.measureText(testText).width;
      
      // If widths are different, the font is available
      const isAvailable = Math.abs(testWidth - fallbackWidth) > 1;
      
      return isAvailable;
      
    } catch (error) {
      return false;
    }
  }

  /**
   * Load font from web font sources
   */
  private async loadWebFont(fontSpec: FontSpec): Promise<LoadedFont | null> {
    // This would integrate with Google Fonts, Adobe Fonts, etc.
    // For now, return null to use fallback
    return null;
  }

  /**
   * Get fallback font when primary font fails
   */
  private getFallbackFont(fontSpec: FontSpec): LoadedFont {
    // Use web-safe font fallbacks based on the requested font type
    const webSafeFallbacks = this.getWebSafeFallback(fontSpec.family);
    
    
    return {
      fontFace: null as any, // Will use CSS font stack instead of FontFace
      family: webSafeFallbacks,
      weight: fontSpec.weight.toString(),
      style: fontSpec.style,
      features: new Set(),
      scripts: new Set(['Latn']),
      status: 'loaded',
      loadTime: 0,
      fileSize: 0
    };
  }

  /**
   * Get web-safe font fallback for a given font family
   */
  private getWebSafeFallback(fontFamily: string): string {
    const lowerFamily = fontFamily.toLowerCase();
    
    // Serif fonts
    if (lowerFamily.includes('times') || lowerFamily.includes('serif') || 
        lowerFamily.includes('garamond') || lowerFamily.includes('minion')) {
      return 'Georgia, "Times New Roman", Times, serif';
    }
    
    // Sans-serif fonts
    if (lowerFamily.includes('arial') || lowerFamily.includes('helvetica') || 
        lowerFamily.includes('sans') || lowerFamily.includes('myriad')) {
      return 'Arial, Helvetica, sans-serif';
    }
    
    // Monospace fonts
    if (lowerFamily.includes('courier') || lowerFamily.includes('mono')) {
      return '"Courier New", Courier, monospace';
    }
    
    // Default to serif for legal documents
    return 'Georgia, "Times New Roman", Times, serif';
  }

  /**
   * Get font key for caching
   */
  private getFontKey(fontSpec: FontSpec): string {
    return `${fontSpec.family}-${fontSpec.weight}-${fontSpec.style}`;
  }

  /**
   * Update loading performance metrics
   */
  private updateLoadingMetrics(loadTime: number): void {
    const totalTime = this.loadingMetrics.averageLoadTime * this.loadingMetrics.totalLoads + loadTime;
    this.loadingMetrics.averageLoadTime = totalTime / (this.loadingMetrics.totalLoads + 1);
  }

  /**
   * Get fonts for specific legal jurisdiction
   */
  public getFontsForJurisdiction(jurisdiction: 'federal' | 'california' | 'newYork' | 'texas' | 'florida'): string[] {
    return this.legalStandards.courtRequiredFonts[jurisdiction] || 
           this.legalStandards.courtRequiredFonts.federal;
  }

  /**
   * Check if font supports specific OpenType features
   */
  public async checkFontFeatures(fontSpec: FontSpec, features: string[]): Promise<string[]> {
    const loadedFont = await this.loadFont(fontSpec);
    return features.filter(feature => loadedFont.features.has(feature));
  }

  /**
   * Get comprehensive font loading metrics
   */
  public getLoadingMetrics() {
    return {
      ...this.loadingMetrics,
      loadedFontsCount: this.loadedFonts.size,
      cacheSize: this.fontCache.size,
      successRate: this.loadingMetrics.totalLoads > 0 
        ? this.loadingMetrics.successfulLoads / this.loadingMetrics.totalLoads 
        : 0
    };
  }

  /**
   * Prepare font for HarfBuzz integration
   */
  public async prepareFontForHarfBuzz(fontSpec: FontSpec): Promise<{
    font: LoadedFont;
    harfbuzzReady: boolean;
    supportedFeatures: string[];
    supportedScripts: string[];
  }> {
    const font = await this.loadFont(fontSpec);
    
    // These will be properly implemented when HarfBuzz is added
    const harfbuzzFeatures = ['kern', 'liga', 'clig', 'smcp', 'c2sc', 'onum', 'tnum'];
    const supportedFeatures = harfbuzzFeatures.filter(feature => font.features.has(feature));
    
    return {
      font,
      harfbuzzReady: supportedFeatures.length > 0,
      supportedFeatures,
      supportedScripts: Array.from(font.scripts)
    };
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.loadedFonts.clear();
    this.loadingPromises.clear();
    this.fontCache.clear();
  }
}
